<template>
  <div class="network-logs-view">
    <v-container>
      <v-row>
        <v-col cols="12">
          <h1>Logs de Red</h1>
          <p>Gestión de logs de eventos de red del sistema</p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          <v-data-table
            :headers="headers"
            :items="networkLogsList"
            :loading="loading"
            class="elevation-1"
            :items-per-page="25"
            :sort-by="[{ key: 'timestamp', order: 'desc' }]"
          >
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Lista de Logs de Red</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <v-spacer></v-spacer>
                <v-btn color="primary" @click="refreshData">
                  <v-icon left>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn color="success" class="ml-2" @click="addItem">
                  <v-icon left>mdi-plus</v-icon>
                  Agregar Log
                </v-btn>
              </v-toolbar>
            </template>
            <template v-slot:item.sourceType="{ item }">
              <v-chip :color="getSourceTypeColor(item.sourceType)" variant="outlined" small>
                {{ getSourceTypeLabel(item.sourceType) }}
              </v-chip>
            </template>
            <template v-slot:item.operation="{ item }">
              <v-chip :color="getOperationColor(item.operation)" variant="outlined" small>
                {{ getOperationLabel(item.operation) }}
              </v-chip>
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="getStatusColor(item.status)" variant="outlined" small>
                {{ getStatusLabel(item.status) }}
              </v-chip>
            </template>
            <template v-slot:item.timestamp="{ item }">
              <span class="font-weight-medium">{{ formatDateTime(item.timestamp) }}</span>
            </template>
            <template v-slot:item.message="{ item }">
              <span class="font-weight-medium">{{ truncateMessage(item.message) }}</span>
            </template>
            <template v-slot:item.errorCode="{ item }">
              <v-chip v-if="item.errorCode" color="red" variant="outlined" small>
                {{ item.errorCode }}
              </v-chip>
              <span v-else class="text-grey">-</span>
            </template>
            <template v-slot:item.latencyMs="{ item }">
              <v-chip :color="getLatencyColor(item.latencyMs)" variant="outlined" small>
                {{ item.latencyMs }} ms
              </v-chip>
            </template>
            <template v-slot:item.notificated="{ item }">
              <v-icon 
                :color="item.notificated ? 'warning' : 'success'" 
                small
              >
                {{ item.notificated ? 'mdi-bell' : 'mdi-bell-off' }}
              </v-icon>
            </template>
            <template v-slot:item.createdAt="{ item }">
              {{ formatDate(item.createdAt) }}
            </template>
            <template v-slot:item.actions="{ item }">
              <v-icon small class="mr-2" @click="editItem(item)">
                mdi-pencil
              </v-icon>
              <v-icon small @click="deleteItem(item)">
                mdi-delete
              </v-icon>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useNetworkLogsStore } from './store'
import { NetworkLogs } from './models/networkLogs'

const networkLogsStore = useNetworkLogsStore()
const loading = ref(false)

const headers = [
  { title: 'ID', key: 'id' },
  { title: 'ID Origen', key: 'sourceId' },
  { title: 'Tipo Origen', key: 'sourceType' },
  { title: 'Operación', key: 'operation' },
  { title: 'Estado', key: 'status' },
  { title: 'Fecha/Hora', key: 'timestamp' },
  { title: 'Mensaje', key: 'message' },
  { title: 'Código Error', key: 'errorCode' },
  { title: 'Latencia', key: 'latencyMs' },
  { title: 'Notificado', key: 'notificated' },
  { title: 'Creado', key: 'createdAt' },
  { title: 'Acciones', key: 'actions', sortable: false },
]

const networkLogsList = computed(() => networkLogsStore.networkLogs)

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('es-ES')
}

const formatDateTime = (date: Date | string) => {
  return new Date(date).toLocaleString('es-ES')
}

const truncateMessage = (message: string) => {
  if (!message) return 'Sin mensaje'
  return message.length > 40 ? message.substring(0, 40) + '...' : message
}

const getSourceTypeColor = (sourceType: string) => {
  switch (sourceType) {
    case 'sensorExtraction':
      return 'blue'
    case 'sensorInjection':
      return 'green'
    case 'deviceExtraction':
      return 'orange'
    default:
      return 'grey'
  }
}

const getSourceTypeLabel = (sourceType: string) => {
  switch (sourceType) {
    case 'sensorExtraction':
      return 'Extracción Sensor'
    case 'sensorInjection':
      return 'Inyección Sensor'
    case 'deviceExtraction':
      return 'Extracción Dispositivo'
    default:
      return sourceType
  }
}

const getOperationColor = (operation: string) => {
  switch (operation) {
    case 'extraction':
      return 'blue'
    case 'injection':
      return 'green'
    default:
      return 'grey'
  }
}

const getOperationLabel = (operation: string) => {
  switch (operation) {
    case 'extraction':
      return 'Extracción'
    case 'injection':
      return 'Inyección'
    default:
      return operation
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'connected':
      return 'success'
    case 'disconnected':
      return 'error'
    case 'timeout':
      return 'warning'
    case 'error':
      return 'red'
    default:
      return 'grey'
  }
}

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'connected':
      return 'Conectado'
    case 'disconnected':
      return 'Desconectado'
    case 'timeout':
      return 'Timeout'
    case 'error':
      return 'Error'
    default:
      return status
  }
}

const getLatencyColor = (latencyMs: number) => {
  if (latencyMs < 100) return 'success'
  if (latencyMs < 500) return 'warning'
  return 'error'
}

const refreshData = async () => {
  loading.value = true
  try {
    await networkLogsStore.getNetworkLogs()
  } finally {
    loading.value = false
  }
}

const addItem = () => {
  // Implementar lógica para agregar log de red
  console.log('Add new network log')
}

const editItem = (item: NetworkLogs) => {
  // Implementar lógica de edición
  console.log('Edit item:', item)
}

const deleteItem = (item: NetworkLogs) => {
  // Implementar lógica de eliminación
  console.log('Delete item:', item)
}

onMounted(() => {
  refreshData()
})
</script>

<style scoped>
.network-logs-view {
  height: 100%;
}
</style>
